import { YourImageItem } from '../types';

const DB_NAME = 'SquareMagnetsDB';
const DB_VERSION = 1;
const STORE_NAME = 'designer_drafts';
const DRAFTS_KEY = 'active_drafts';
const SELECTED_ID_KEY = 'selected_draft_id';

function openDB(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      resolve(null);
      return;
    }
    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = (event) => {
        resolve((event.target as IDBOpenDBRequest).result);
      };
      request.onerror = () => {
        resolve(null);
      };
    } catch (e) {
      resolve(null);
    }
  });
}

export async function saveDesignerDrafts(drafts: YourImageItem[]): Promise<void> {
  if (!drafts || drafts.length === 0) {
    await clearDesignerDrafts();
    return;
  }

  // First try saving to IndexedDB (supports large Base64 / multi-megabyte image layers)
  try {
    const db = await openDB();
    if (db) {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(drafts, DRAFTS_KEY);
      await new Promise<void>((resolve) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    }
  } catch (e) {
    console.warn('IndexedDB save draft failed:', e);
  }

  // Also save a safe lightweight version to localStorage as backup
  try {
    const safeBackup = drafts
      .slice(0, 5)
      .map(item => {
        const safeImgUrl = (item.imageUrl && item.imageUrl.length < 500000)
          ? item.imageUrl
          : ((item.croppedImageUrl && item.croppedImageUrl.length < 500000) ? item.croppedImageUrl : '');
        const safeCroppedUrl = (item.croppedImageUrl && item.croppedImageUrl.length < 500000)
          ? item.croppedImageUrl
          : safeImgUrl;
        return {
          ...item,
          croppedImageUrl: safeCroppedUrl || undefined,
          imageUrl: safeImgUrl,
          overlayImageUrl: (item.overlayImageUrl && item.overlayImageUrl.length < 500000) ? item.overlayImageUrl : '',
        };
      })
      .filter(item => Boolean(item.imageUrl || item.croppedImageUrl || item.overlayImageUrl));
    localStorage.setItem('square_magnets_designer_drafts', JSON.stringify(safeBackup));
  } catch (e) {}
}

export async function removeDesignerDraftById(id: string): Promise<void> {
  if (!id) return;
  try {
    const existing = await loadDesignerDrafts();
    const filtered = existing.filter(item => item.id !== id);
    if (filtered.length === 0) {
      await clearDesignerDrafts();
    } else {
      await saveDesignerDrafts(filtered);
    }
  } catch (e) {}
}

export async function loadDesignerDrafts(): Promise<YourImageItem[]> {
  // First attempt from IndexedDB
  try {
    const db = await openDB();
    if (db) {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(DRAFTS_KEY);
      const result = await new Promise<YourImageItem[] | null>((resolve) => {
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => resolve(null);
      });
      if (result && Array.isArray(result) && result.length > 0) {
        return result.filter(item => !item.isMomentsTemplate && (item.imageUrl || item.croppedImageUrl || item.overlayImageUrl));
      }
    }
  } catch (e) {
    console.warn('IndexedDB load draft failed:', e);
  }

  // Fallback to localStorage
  try {
    const cached = localStorage.getItem('square_magnets_designer_drafts');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter(item => !item.isMomentsTemplate && (item.imageUrl || item.croppedImageUrl || item.overlayImageUrl));
      }
    }
  } catch (e) {}

  return [];
}

export async function clearDesignerDrafts(): Promise<void> {
  try {
    const db = await openDB();
    if (db) {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(DRAFTS_KEY);
      store.delete(SELECTED_ID_KEY);
    }
  } catch (e) {}

  try {
    localStorage.removeItem('square_magnets_designer_drafts');
    localStorage.removeItem('square_magnets_designer_selected_id');
  } catch (e) {}
}

export async function saveSelectedDraftId(id: string): Promise<void> {
  try {
    const db = await openDB();
    if (db) {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      if (id) {
        store.put(id, SELECTED_ID_KEY);
      } else {
        store.delete(SELECTED_ID_KEY);
      }
    }
  } catch (e) {}

  try {
    if (id) {
      localStorage.setItem('square_magnets_designer_selected_id', id);
    } else {
      localStorage.removeItem('square_magnets_designer_selected_id');
    }
  } catch (e) {}
}

export async function loadSelectedDraftId(): Promise<string> {
  try {
    const db = await openDB();
    if (db) {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(SELECTED_ID_KEY);
      const result = await new Promise<string | null>((resolve) => {
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => resolve(null);
      });
      if (result) return result;
    }
  } catch (e) {}

  try {
    return localStorage.getItem('square_magnets_designer_selected_id') || '';
  } catch (e) {}

  return '';
}
